/**
 * Radar Provider Utilities
 * Handles tile URL construction for weather.com, NWS WMS and RainViewer radar sources
 */

const RADAR_TILE_URL = import.meta.env.VITE_RADAR_TILE_URL || 'https://ro-tiles.rainowls.workers.dev';

/** CONUS bounding box (Continental United States) */
export const CONUS_BOUNDS = {
  minLat: 24,
  maxLat: 50,
  minLng: -125,
  maxLng: -66,
} as const;

/** Radar timestamp with source metadata */
export type RadarSource = 'weathercom' | 'nws' | 'rainviewer';

export interface RadarTimestamp {
  source: RadarSource;
  epoch: number;
  isoString?: string;
  tileUrlBase?: string;
}

/**
 * Check if a lat/lng coordinate is within CONUS bounds
 */
export function isWithinCONUS(lat: number, lng: number): boolean {
  return (
    lat >= CONUS_BOUNDS.minLat &&
    lat <= CONUS_BOUNDS.maxLat &&
    lng >= CONUS_BOUNDS.minLng &&
    lng <= CONUS_BOUNDS.maxLng
  );
}

/**
 * Convert Google Maps tile coordinates to EPSG:3857 (Web Mercator) BBOX
 * Used for WMS GetMap requests
 */
export function tileToWebMercatorBBox(
  x: number,
  y: number,
  zoom: number
): { minX: number; minY: number; maxX: number; maxY: number } {
  const originShift = 20037508.342789244;
  const tileSize = (2 * originShift) / Math.pow(2, zoom);

  const minX = x * tileSize - originShift;
  const maxX = (x + 1) * tileSize - originShift;
  const minY = originShift - (y + 1) * tileSize;
  const maxY = originShift - y * tileSize;

  return { minX, minY, maxX, maxY };
}

/**
 * Build a NWS WMS tile URL for a given tile coordinate and timestamp
 */
export function buildNWSWMSTileUrl(
  coord: { x: number; y: number },
  zoom: number,
  isoTimestamp: string
): string {
  const bbox = tileToWebMercatorBBox(coord.x, coord.y, zoom);
  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: '1.1.1',
    REQUEST: 'GetMap',
    FORMAT: 'image/png',
    TRANSPARENT: 'true',
    LAYERS: 'conus_bref_qcd',
    TIME: isoTimestamp,
    WIDTH: '256',
    HEIGHT: '256',
    SRS: 'EPSG:3857',
    BBOX: `${bbox.minX},${bbox.minY},${bbox.maxX},${bbox.maxY}`,
  });

  return `https://opengeo.ncep.noaa.gov/geoserver/conus/conus_bref_qcd/ows?${params.toString()}`;
}

/**
 * Convert Web Mercator (EPSG:3857) coordinates to lat/lng (WGS84).
 */
export function webMercatorToLatLng(x: number, y: number): { lat: number; lng: number } {
  const originShift = 20037508.342789244;
  const lng = (x / originShift) * 180;
  let lat = (y / originShift) * 180;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
  return { lat, lng };
}

/**
 * Build a weather.com radar tile URL via the ro-tiles proxy
 */
export function buildWeathercomTileUrl(
  coord: { x: number; y: number },
  zoom: number,
  epoch: number
): string {
  return `${RADAR_TILE_URL}/tile/${zoom}/${coord.x}/${coord.y}?product=twcRadarMosaic&ts=${epoch}`;
}

/**
 * Build a RainViewer tile URL for a given tile coordinate and epoch timestamp
 */
export function buildRainViewerTileUrl(
  coord: { x: number; y: number },
  zoom: number,
  tileUrlBase: string
): string {
  return `${tileUrlBase}/256/${zoom}/${coord.x}/${coord.y}/2/1_1.png`;
}
